import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Filter } from "lucide-react";

const CATEGORY_LABELS = {
  "booster-boxes": "Booster Boxes",
  "elite-trainer-boxes": "Elite Trainer Boxes",
  "single-cards": "Single Cards",
  "plushies": "Plushies",
  "sleeves": "Card Sleeves",
  "toploaders": "Toploaders",
  "deck-boxes": "Deck Boxes",
  "binders": "Binders",
};

export default function Shop() {
  const { category } = useParams();
  const [params] = useSearchParams();
  const q = params.get("q") || "";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    setLoading(true);
    const url = category ? `/products?category=${category}` : "/products";
    api
      .get(url + (q ? `${url.includes("?") ? "&" : "?"}q=${encodeURIComponent(q)}` : ""))
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, q]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price-asc") arr.sort((a, b) => a.price_cents - b.price_cents);
    else if (sort === "price-desc") arr.sort((a, b) => b.price_cents - a.price_cents);
    else if (sort === "name") arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [products, sort]);

  const title = q
    ? `Search: "${q}"`
    : category
      ? CATEGORY_LABELS[category] || category
      : "Shop All";

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12">
      {/* Breadcrumb */}
      <div className="text-[11px] tracking-[0.22em] uppercase text-white/40 font-mono mb-3">
        <Link to="/" className="hover:text-white">Home</Link> / <Link to="/shop" className="hover:text-white">Shop</Link>
        {category && <> / {CATEGORY_LABELS[category] || category}</>}
      </div>
      <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter text-white">{title}</h1>
      <div className="mt-2 text-sm text-white/50">{sorted.length} {sorted.length === 1 ? "product" : "products"}</div>

      <div className="mt-10 grid grid-cols-12 gap-8">
        {/* Sidebar — categories */}
        <aside className="col-span-12 md:col-span-3 lg:col-span-2">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#B026FF] font-bold mb-4 flex items-center gap-2">
            <Filter size={12} /> Categories
          </div>
          <div className="space-y-2">
            <Link
              to="/shop"
              data-testid="filter-all"
              className={`block text-sm py-1 ${!category ? "text-[#B026FF]" : "text-white/70 hover:text-white"}`}
            >
              All Products
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/shop/${c.slug}`}
                data-testid={`filter-${c.slug}`}
                className={`block text-sm py-1 ${category === c.slug ? "text-[#B026FF]" : "text-white/70 hover:text-white"}`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </aside>

        {/* Grid */}
        <div className="col-span-12 md:col-span-9 lg:col-span-10">
          <div className="flex items-center justify-end mb-6">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="shop-sort"
              className="bg-[#0A0812] border border-white/10 text-white/80 rounded-md px-3 py-2 text-sm focus:border-[#B026FF]/60 outline-none"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name">Name</option>
            </select>
          </div>

          {loading ? (
            <div className="text-white/50">Loading…</div>
          ) : sorted.length === 0 ? (
            <div className="card-elev rounded-xl p-12 text-center text-white/60">
              No products found{q && ` matching "${q}"`}.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {sorted.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
